// src/components/UserMeet/User-Meet-Private/BreakoutInviteModal.jsx
import React, { useEffect, useState } from "react";
import { X, Users, Clock, User } from "lucide-react";

export default function BreakoutInviteModal({
  invite,
  onAccept,
  onReject,
  responding,
}) {
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (!invite) {
      setTimeRemaining(null);
      return;
    }

    const updateTimeRemaining = () => {
      if (!invite?.timeLimitMinutes) return;
      
      const now = new Date();
      let remainingMs;
      
      if (invite.expiresAt) {
        const expiresAt = new Date(invite.expiresAt);
        remainingMs = expiresAt - now;
      } else {
        remainingMs = invite.timeLimitMinutes * 60 * 1000;
      }

      const remainingMinutes = Math.max(0, Math.floor(remainingMs / 60000));
      setTimeRemaining(remainingMinutes);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 30000);

    return () => clearInterval(interval);
  }, [invite]);

  if (!invite) return null;

  const ownerName = invite.ownerName || "Someone";
  const ownerImage = invite.ownerImage || null;
  const message = invite.message || `${ownerName} invited you to a private breakout room`;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[9999] p-5 animate-fadeIn">
      <div 
        className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col border border-blue-500/10 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/15 to-indigo-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">Breakout Room Invitation</h2>
              <p className="text-sm text-white/60">You've been invited to a private conversation</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {/* Inviter Info */}
          <div className="flex items-center gap-4 p-4 bg-white/3 border border-white/10 rounded-xl mb-5">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white flex-shrink-0 overflow-hidden">
              {ownerImage ? (
                <img src={ownerImage} alt={ownerName} className="w-full h-full object-cover" />
              ) : (
                <User size={32} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white mb-1">{ownerName}</p>
              <p className="text-sm text-white/60 line-clamp-2">{message}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3 mb-5">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-white/3 border border-white/10 rounded-lg text-sm text-white/80">
              <Clock size={16} className="text-green-400 flex-shrink-0" />
              <span className="flex-1">
                Duration: {invite.timeLimitMinutes || 15} minutes
              </span>
              {timeRemaining !== null && (
                <span className="px-2.5 py-1 bg-yellow-500/15 border border-yellow-500/30 rounded-md text-yellow-400 text-xs font-semibold">
                  ~{timeRemaining}m left
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3 px-3 py-2.5 bg-white/3 border border-white/10 rounded-lg text-sm text-white/80">
              <Users size={16} className="text-green-400 flex-shrink-0" />
              <span>Private breakout room</span>
            </div>
          </div>

          {/* Info */}
          <div className="flex gap-3 p-3 bg-blue-500/8 border border-blue-500/20 rounded-lg text-sm text-white/70">
            <i className="fa-solid fa-circle-info text-blue-400 flex-shrink-0 mt-0.5" />
            <p>
              Joining will move you to a private sub-room. You can return to the main meeting anytime.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 flex items-center gap-3">
          <button
            onClick={onReject}
            disabled={responding}
            type="button"
            className="flex-1 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/80 font-semibold text-sm hover:bg-white/10 hover:border-white/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {responding ? "Declining..." : "Decline"}
          </button>
          <button
            onClick={onAccept}
            disabled={responding}
            type="button"
            className="flex-1 px-5 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 border border-green-500/50 text-white font-semibold text-sm hover:from-emerald-500 hover:to-green-600 hover:shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {responding ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <Users size={16} />
                Join Breakout
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}